import { useNavigate } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { Avatar } from "@/components/bits";
import { api, ApiError } from "@/lib/api";
import type { DeviceFamilyDTO } from "@shared/types";

/** This browser's paired family, or null. Only a paired device (an httpOnly cookie) gets the kid list. */
export function useKidDevice() {
  return useQuery<DeviceFamilyDTO | null, ApiError>({
    queryKey: ["kid-device"],
    queryFn: () =>
      api.get<DeviceFamilyDTO>("/auth/device").catch((err: ApiError) => {
        if (err.status === 404) return null;
        throw err;
      }),
    retry: false,
  });
}

/** On a paired device: the family's kids, one tap from their PIN pad. Renders nothing elsewhere. */
export function PairedKids({ className }: { className?: string }) {
  const device = useKidDevice();
  const navigate = useNavigate();
  const d = device.data;
  if (!d || !d.consentGranted || d.kids.length === 0) return null;
  return (
    <section className={className} aria-label="Kids on this device">
      <h2 className="text-lg font-extrabold">Kids, tap your name</h2>
      <div className="-mx-1 mt-2 flex gap-2 overflow-x-auto px-1 pb-1">
        {d.kids.map((k) => (
          <button
            key={k.id}
            type="button"
            onClick={() => navigate(`/kid-login?kid=${encodeURIComponent(k.id)}`)}
            className="flex w-24 shrink-0 flex-col items-center gap-1 rounded-3xl bg-card p-3 shadow-card ring-1 ring-line transition active:scale-95"
          >
            <Avatar member={k} size="md" />
            <span className="w-full truncate text-center font-extrabold">{k.name}</span>
          </button>
        ))}
      </div>
    </section>
  );
}
